// Orkestrator E0 §6 — pure guard against pasted subscription tokens. No server, env or DB imports:
// unit tests and client code load it without `.env`; `byoa.server.ts` wraps the error into HttpError.

/**
 * Bystep stores API keys, never a vendor login. A pasted value that is really a subscription
 * token is refused outright — shape only, nothing is decoded or looked up:
 *   - Claude Code OAuth access tokens start with `sk-ant-oat`
 *   - any JSON carrying `access_token` / `refresh_token` / `id_token` (OAuth credential files)
 *   - Codex `auth.json` starts with `{"OPENAI_API_KEY"`
 */
export const SUBSCRIPTION_TOKEN_REJECTED = 'subscription_token_rejected' as const

export function looksLikeSubscriptionToken(value: unknown): boolean {
  if (typeof value !== 'string') return false
  const v = value.trim()
  if (!v) return false
  if (/^sk-ant-oat/i.test(v)) return true
  if (v.startsWith('{"OPENAI_API_KEY"')) return true
  if ((v.startsWith('{') || v.startsWith('[')) && /"(access_token|refresh_token|id_token)"\s*:/i.test(v)) return true
  return false
}

/** Same shape as HttpError (`status`, `code`) so route handlers can pass it through; the server wraps it with the localized message. */
export class SubscriptionTokenRejectedError extends Error {
  readonly status = 400
  readonly code = SUBSCRIPTION_TOKEN_REJECTED
  constructor(message = 'This looks like a subscription login token, not an API key. Bystep never stores subscription tokens.') {
    super(message)
    this.name = 'SubscriptionTokenRejectedError'
  }
}

/** Throws `SubscriptionTokenRejectedError` (status 400, code `subscription_token_rejected`). */
export function assertNotSubscriptionToken(value: unknown): void {
  if (looksLikeSubscriptionToken(value)) throw new SubscriptionTokenRejectedError()
}
